import { useState, useEffect, useRef } from 'react';
import { IdentificationCard, Upload, Warning, Check, X, Spinner } from '@phosphor-icons/react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useLocationContext } from '../contexts/LocationContext';

const INP = {
  width: '100%', background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)',
  padding: '8px 10px', color: 'hsl(0,0%,85%)', fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11, outline: 'none', boxSizing: 'border-box',
};
const LBL = {
  display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
  color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4,
};
const SECTION = {
  background: 'hsl(0,0%,4%)', border: '1px solid hsl(0,0%,14%)', padding: 20, marginBottom: 16,
};
const SECTION_TITLE = {
  fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)',
  textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: 14,
  borderBottom: '1px solid hsl(0,0%,12%)', paddingBottom: 8,
};

export default function CompanyProfile() {
  const { user } = useAuth();
  const { refreshOrg } = useLocationContext();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const fileRef = useRef(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    api.get('/organization').then(r => {
      setOrg(r.data);
      setLogoPreview(r.data?.logo_url || null);
    }).catch(() => showToast('Failed to load company profile', 'error'))
      .finally(() => setLoading(false));
  }, []);

  if (user?.role !== 'admin') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 16 }}>
        <Warning size={48} color="hsl(0,84%,60%)" />
        <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 18, color: 'hsl(0,0%,60%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Access Restricted</div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'hsl(0,0%,40%)' }}>Company profile is accessible to System Admins only.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh', gap: 10, color: 'hsl(0,0%,45%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
        <Spinner size={18} style={{ animation: 'spin 1s linear infinite' }} /> Loading...
      </div>
    );
  }

  const set = (path, value) => {
    setOrg(prev => {
      const next = { ...prev };
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(file.type)) {
      showToast('Please select PNG, JPG, WebP or SVG', 'error'); return;
    }
    if (file.size > 2 * 1024 * 1024) { showToast('Logo must be under 2 MB', 'error'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Upload logo first if changed
      if (logoFile) {
        const fd = new FormData();
        fd.append('file', logoFile);
        const res = await api.post('/organization/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        set('logo_url', res.data.logo_url);
        setLogoFile(null);
      }
      // Save profile
      await api.put('/organization', org);
      await refreshOrg();
      showToast('Company profile saved successfully');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="company-profile-page">
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '12px 20px',
          background: toast.type === 'error' ? 'hsl(0,80%,45%)' : 'hsl(142,70%,40%)',
          color: '#fff', fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
          letterSpacing: '0.05em', boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {toast.type === 'error' ? <X size={14} /> : <Check size={14} weight="bold" />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700, color: 'hsl(0,0%,98%)', textTransform: 'uppercase', marginBottom: 4 }}>
            Company Profile
          </h1>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            Branding · Legal Details · Preferences
          </div>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: saving ? 0.7 : 1 }}>
          {saving ? <Spinner size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={13} weight="bold" />}
          {saving ? 'SAVING...' : 'SAVE CHANGES'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* Left column */}
        <div>
          {/* Logo */}
          <div style={SECTION}>
            <div style={SECTION_TITLE}>Brand Logo</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{
                width: 80, height: 80, border: '1px solid hsl(0,0%,20%)',
                background: 'hsl(0,0%,7%)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', overflow: 'hidden', flexShrink: 0,
              }}>
                {logoPreview
                  ? <img src={logoPreview} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  : <IdentificationCard size={32} color="hsl(0,0%,28%)" />
                }
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={handleLogoChange} style={{ display: 'none' }} />
                <button onClick={() => fileRef.current?.click()} className="btn-ghost" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <Upload size={13} /> Upload Logo
                </button>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,35%)' }}>
                  PNG, JPG, WebP or SVG · Max 2 MB
                </div>
              </div>
            </div>
          </div>

          {/* Legal Details */}
          <div style={SECTION}>
            <div style={SECTION_TITLE}>Legal Details</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={LBL}>Legal Name *</label>
                <input style={INP} value={org?.legal_name || ''} onChange={e => set('legal_name', e.target.value)} placeholder="e.g. Aria Forge Pvt. Ltd." />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={LBL}>Trade / Brand Name</label>
                <input style={INP} value={org?.trade_name || ''} onChange={e => set('trade_name', e.target.value)} placeholder="e.g. ARIA FORGE" />
              </div>
              <div>
                <label style={LBL}>PAN</label>
                <input style={INP} value={org?.pan || ''} onChange={e => set('pan', e.target.value.toUpperCase())} placeholder="AAAAA1234A" maxLength={10} />
              </div>
              <div>
                <label style={LBL}>CIN</label>
                <input style={INP} value={org?.cin || ''} onChange={e => set('cin', e.target.value.toUpperCase())} placeholder="U74900MH2020PTC345678" />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={LBL}>Primary GSTIN</label>
                <input style={INP} value={org?.gstin_primary || ''} onChange={e => set('gstin_primary', e.target.value.toUpperCase())} placeholder="27AAAAA1234A1Z5" maxLength={15} />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div style={SECTION}>
            <div style={SECTION_TITLE}>Contact</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={LBL}>Phone</label>
                <input style={INP} value={org?.phone || ''} onChange={e => set('phone', e.target.value)} placeholder="+91 20 1234 5678" />
              </div>
              <div>
                <label style={LBL}>Email</label>
                <input style={INP} type="email" value={org?.email || ''} onChange={e => set('email', e.target.value)} placeholder="info@company.com" />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={LBL}>Website</label>
                <input style={INP} value={org?.website || ''} onChange={e => set('website', e.target.value)} placeholder="https://www.company.com" />
              </div>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div>
          {/* Branding */}
          <div style={SECTION}>
            <div style={SECTION_TITLE}>Branding</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              {[
                { key: 'primary_color', label: 'Primary Color' },
                { key: 'secondary_color', label: 'Secondary Color' },
                { key: 'accent_color', label: 'Accent Color' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label style={LBL}>{label}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="color"
                      value={org?.branding?.[key] || '#00C8FF'}
                      onChange={e => set(`branding.${key}`, e.target.value)}
                      style={{ width: 36, height: 30, border: '1px solid hsl(0,0%,18%)', background: 'none', cursor: 'pointer', padding: 2 }}
                    />
                    <input
                      style={{ ...INP, flex: 1, fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}
                      value={org?.branding?.[key] || ''}
                      onChange={e => set(`branding.${key}`, e.target.value)}
                      placeholder="#00C8FF"
                    />
                  </div>
                </div>
              ))}
            </div>
            <div>
              <label style={LBL}>Tagline</label>
              <input style={INP} value={org?.branding?.tagline || ''} onChange={e => set('branding.tagline', e.target.value)} placeholder="e.g. Precision Manufacturing" />
            </div>
          </div>

          {/* Registered Address */}
          <div style={SECTION}>
            <div style={SECTION_TITLE}>Registered Address</div>
            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <label style={LBL}>Address Line 1</label>
                <input style={INP} value={org?.registered_address?.line1 || ''} onChange={e => set('registered_address.line1', e.target.value)} placeholder="Street / Building" />
              </div>
              <div>
                <label style={LBL}>Address Line 2</label>
                <input style={INP} value={org?.registered_address?.line2 || ''} onChange={e => set('registered_address.line2', e.target.value)} placeholder="Area / Locality" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <div>
                  <label style={LBL}>City</label>
                  <input style={INP} value={org?.registered_address?.city || ''} onChange={e => set('registered_address.city', e.target.value)} placeholder="Pune" />
                </div>
                <div>
                  <label style={LBL}>State</label>
                  <input style={INP} value={org?.registered_address?.state || ''} onChange={e => set('registered_address.state', e.target.value)} placeholder="Maharashtra" />
                </div>
                <div>
                  <label style={LBL}>PIN</label>
                  <input style={INP} value={org?.registered_address?.pincode || ''} onChange={e => set('registered_address.pincode', e.target.value)} placeholder="411001" maxLength={6} />
                </div>
              </div>
              <div>
                <label style={LBL}>Country</label>
                <input style={INP} value={org?.registered_address?.country || 'India'} onChange={e => set('registered_address.country', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div style={SECTION}>
            <div style={SECTION_TITLE}>Preferences</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={LBL}>Currency</label>
                <select style={{ ...INP }} value={org?.preferences?.currency || 'INR'} onChange={e => set('preferences.currency', e.target.value)}>
                  {['INR', 'USD', 'EUR', 'GBP', 'AED'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={LBL}>Timezone</label>
                <select style={{ ...INP }} value={org?.preferences?.timezone || 'Asia/Kolkata'} onChange={e => set('preferences.timezone', e.target.value)}>
                  {['Asia/Kolkata', 'UTC', 'America/New_York', 'Europe/London', 'Asia/Dubai'].map(tz => <option key={tz}>{tz}</option>)}
                </select>
              </div>
              <div>
                <label style={LBL}>Date Format</label>
                <select style={{ ...INP }} value={org?.preferences?.date_format || 'DD/MM/YYYY'} onChange={e => set('preferences.date_format', e.target.value)}>
                  {['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'].map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label style={LBL}>FY Start Month</label>
                <select style={{ ...INP }} value={org?.preferences?.fy_start_month || 4} onChange={e => set('preferences.fy_start_month', parseInt(e.target.value))}>
                  {[['1','January'],['4','April'],['7','July'],['10','October']].map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
